/**
 * Block 13 PR-G Phase 4B — Section «Реквизиты сторон».
 *
 * Renders 2 anketas (Customer + Executor) с P-009 hint footnote под Customer-side.
 * Reducer actions ADD_REQUISITE_ROW / UPDATE_REQUISITE / REMOVE_REQUISITE_ROW
 * dispatched per side; sub-handler partial application keeps RequisitesAnketa
 * presentational.
 *
 * **P-009 visible hint (PRD §10.13.2 «Защита ПДн»):** Customer-side содержит
 * чувствительные данные (паспорт / ИИН / адрес). Phase 7.1 реализовал
 * auto-clear customer values после PDF Download/Share (P-009 strict).
 *
 * **Phase 7 hotfix round 4 (ADR-0025 A9, 2026-05-26):** добавлен opt-in
 * checkbox «Сохранять данные клиента для повторного создания договоров» под
 * P-009 hint (default OFF). При ON — мебельщик-«серийник» сохраняет customer
 * data для повторных договоров; auto-clear conditional через
 * `state.preserveCustomerRequisites` flag.
 *
 * **Executor-side** не показывает P-009 hint — реквизиты исполнителя — это
 * собственные данные пользователя приложения (мебельщика), не transient.
 *
 * Reference:
 *   - prototype `screen-2.13.2-contract.html` lines 482-497 (Реквизиты card).
 *   - PRD §10.13.2 «Защита ПДн (P-009 — критично!)»
 *   - ADR-0025 §Q6 (transient PII flow) + Amendment A9 (opt-in checkbox).
 */

import { type Dispatch } from 'react';

import { genUuid } from '@/lib/uuid';
import { SectionCard } from '@/features/calculator/anketa/components/SectionCard';
import { SectionHeader } from '@/features/calculator/anketa/components/SectionHeader';

import { RequisitesAnketa } from '../components/RequisitesAnketa';
import type { ContractAction, ContractFormState, RequisiteSide } from '../state/contractReducer';

const SUBHEADING_CLASS = 'text-xs font-bold text-ink uppercase tracking-wide mb-2';

export interface SectionRequisitesProps {
  state: ContractFormState;
  dispatch: Dispatch<ContractAction>;
}

export function SectionRequisites({ state, dispatch }: SectionRequisitesProps) {
  function makeSideHandlers(side: RequisiteSide) {
    return {
      onUpdateValue: (rowId: string, value: string) =>
        dispatch({
          type: 'UPDATE_REQUISITE',
          side,
          update: { rowId, field: 'value', value },
        }),
      onUpdateLabel: (rowId: string, value: string) =>
        dispatch({
          type: 'UPDATE_REQUISITE',
          side,
          update: { rowId, field: 'label', value },
        }),
      onRemove: (rowId: string) => dispatch({ type: 'REMOVE_REQUISITE_ROW', side, rowId }),
      onAdd: () => dispatch({ type: 'ADD_REQUISITE_ROW', side, id: genUuid() }),
    };
  }

  const customerHandlers = makeSideHandlers('customer');
  const executorHandlers = makeSideHandlers('executor');

  return (
    <SectionCard testId="contract-section-requisites">
      <SectionHeader>Реквизиты</SectionHeader>

      <div className="pb-3.5 mb-3.5 border-b border-accent/30">
        <p className={SUBHEADING_CLASS}>Заказчик</p>
        <RequisitesAnketa
          rows={state.customerRequisites}
          sideLabel="заказчика"
          testIdPrefix="customer-requisites"
          {...customerHandlers}
        />
        {/* Phase 7 hotfix round 5 (R5-#2, 2026-05-26) — Aibek shortened text:
            один short sentence без ссылки на закон, без инструкции про
            чекбокс, без ⚠ префикса. */}
        <p
          className="mt-3 text-xs text-ink/60 leading-snug"
          data-testid="contract-pii-hint"
          role="note"
        >
          По умолчанию данные клиента автоматически очищаются после создания Договора
        </p>
        <label
          className="mt-3 flex items-start gap-2 min-h-tap cursor-pointer"
          data-testid="contract-preserve-customer-toggle"
        >
          <input
            type="checkbox"
            checked={state.preserveCustomerRequisites}
            onChange={(e) =>
              dispatch({
                type: 'SET_FIELD',
                update: { path: 'preserveCustomerRequisites', value: e.target.checked },
              })
            }
            className="mt-0.5 w-5 h-5 accent-cta cursor-pointer touch-manipulation"
            aria-label="Сохранять данные клиента для повторного создания договоров"
          />
          <span className="text-sm text-ink leading-snug">
            Сохранять данные клиента для повторного создания договоров
          </span>
        </label>
      </div>

      <div>
        <p className={SUBHEADING_CLASS}>Исполнитель</p>
        <RequisitesAnketa
          rows={state.executorRequisites}
          sideLabel="исполнителя"
          testIdPrefix="executor-requisites"
          {...executorHandlers}
        />
      </div>
    </SectionCard>
  );
}
